// Promotion Location Item Service - API interactions for promotion location item management
import {
  PromotionLocationItem,
  PromotionLocationItemPageParams,
  PromotionLocationItemListResponse,
  PromotionLocationItemImportData,
  PromotionLocationItemImportResult,
  ApiResponse,
} from '../types/promotionLocationItem';
import { Item } from '../types/item';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Promotion Location Item operations
 * Supports both Mock data and real API calls
 */
export class PromotionLocationItemService {
  private static instance: PromotionLocationItemService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;


  /**
   * Get singleton instance
   */
  public static getInstance(): PromotionLocationItemService {
    if (!PromotionLocationItemService.instance) {
      PromotionLocationItemService.instance = new PromotionLocationItemService();
    }
    return PromotionLocationItemService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    PromotionLocationItemService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return PromotionLocationItemService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 分页查询促销位货品
   */
  async pageQueryPromotionLocationItems(params: PromotionLocationItemPageParams): Promise<ApiResponse<PromotionLocationItemListResponse>> {
    try {
      if (PromotionLocationItemService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(800);
        
        // Import mock data dynamically
        const { mockPageQueryPromotionLocationItems } = await import('../mock/promotionLocationItemData');
        return mockPageQueryPromotionLocationItems(params);
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.PROMOTION_LOCATION_ITEM.PAGE, 
          params
        );
        return response.data;
      }
    } catch (error) {
      console.error('分页查询促销位货品失败:', error);
      return createErrorResponse('SERVER_ERROR', 'PROMOTION_LOCATION_ITEM_QUERY_FAILED');
    }
  }

  /**
   * 获取商品列表（用于下拉选择）
   */
  async getItemList(): Promise<ApiResponse<Item[]>> {
    try {
      if (PromotionLocationItemService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(500);
        
        // Import mock data dynamically
        const { mockGetItemList } = await import('../mock/promotionLocationItemData');
        return mockGetItemList();
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.ITEM.LIST,
          {}
        );
        return response.data;
      }
    } catch (error) {
      console.error('获取商品列表失败:', error);
      return createErrorResponse('SERVER_ERROR', 'ITEM_LIST_QUERY_FAILED');
    }
  }

  /**
   * Excel导入促销位货品
   */
  async importPromotionLocationItems(file: File): Promise<ApiResponse<PromotionLocationItemImportResult>> {
    try {
      if (PromotionLocationItemService.useMock && WHOLE_USE_MOCK) {
        // // Simulate API delay for import
        // await this.delay(2000);
        
        // // Import mock data dynamically
        // const { mockImportPromotionLocationItems } = await import('../mock/promotionLocationItemData');
        // return mockImportPromotionLocationItems(importData);
      } else {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await httpClient.post(API_ENDPOINTS.PROMOTION_LOCATION_ITEM.IMPORT, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      }
    } catch (error) {
      console.error('Excel导入促销位货品失败:', error);
      return createErrorResponse('SERVER_ERROR', 'PROMOTION_LOCATION_ITEM_IMPORT_FAILED');
    }
  }

  /**
   * 下载Excel导入模板
   */
  async downloadImportTemplate(): Promise<Blob> {
    try {
      if (PromotionLocationItemService.useMock && WHOLE_USE_MOCK) {
        // 模拟Excel模板文件
        const csvContent = "商品编码,位置编码\nITEM001,LOC-A001\nITEM002,LOC-A002";
        return new Blob(["\ufeff" + csvContent], { 
          type: 'text/csv;charset=utf-8;' 
        });
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.PROMOTION_LOCATION_ITEM.DOWNLOAD_TEMPLATE,
          {}
        );
        return response.data;
      }
    } catch (error) {
      console.error('下载Excel导入模板失败:', error);
      throw new Error('下载Excel导入模板失败');
    }
  }

  /**
   * 验证导入数据
   */
  async validateImportData(importData: PromotionLocationItemImportData[]): Promise<ApiResponse<{ isValid: boolean; errors: string[] }>> {
    try {
      if (PromotionLocationItemService.useMock && WHOLE_USE_MOCK) {
        // Simulate validation delay
        await this.delay(1000);
        
        // Mock validation logic
        const errors: string[] = [];
        
        importData.forEach((item, index) => {
          if (!item.itemCode || item.itemCode.trim() === '') {
            errors.push(`第${index + 1}行：商品编码不能为空`);
          }
          if (!item.locationCode || item.locationCode.trim() === '') {
            errors.push(`第${index + 1}行：位置编码不能为空`);
          }
        });
        
        return {
          traceId: `mock-${Date.now()}`,
          success: true,
          flag: 'success',
          data: {
            isValid: errors.length === 0,
            errors,
          },
        };
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.PROMOTION_LOCATION_ITEM.VALIDATE,
          { data: importData }
        );
        return response.data;
      }
    } catch (error) {
      console.error('验证导入数据失败:', error);
      return createErrorResponse('SERVER_ERROR', 'PROMOTION_LOCATION_ITEM_VALIDATE_FAILED');
    }
  }
}

// Export singleton instance
const promotionLocationItemService = PromotionLocationItemService.getInstance();
export default promotionLocationItemService;